import * as React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { isComplianceRegionCHAS } from '@pec/aion-ui-core/helpers/complianceRegion';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { useUserPermission } from './useUserPermission';

type UseUserMenuLinkResult = {
  complyWorksApplicationSwitcherLink: string;
  instructorPortalLink: string;
  verisourceApplicationSwitcherLink: string;
  shouldShowApplicationSwitcher: boolean;
  shouldShowInstructorPortal: boolean;
};

type ReadonlyUserOrganization = {
  id: string;
  isPrimary: boolean;
  features: ReadonlyArray<OrganizationFeature>;
};

const { CProNavigationToVeriforceOne, Client, PrimeContractor, Subcontractor, Subscriber } = OrganizationFeature;

const vf1ComplyWorksAccountSelectorHint = '/?hint=vf1-complyworks-account-selector';
const vf1VeriSourceAccountSelectorHint = '/?hint=vf1-verisource-account-selector';
const { Manage } = ActivityAction;
const { Organizations, Classes } = ActivityResourceName;

const instructorPortalRoute = '/InstructorPortal';
const instructorMarketingPageRoute = 'https://veriforce.com/solutions/become-an-instructor';

export const resolveApplicationSwitcherLink = (organizationId?: string): string =>
  organizationId
    ? `/selectOrganization?organizationId=${encodeURIComponent(organizationId)}&returnTo=${encodeURIComponent(
        vf1ComplyWorksAccountSelectorHint
      )}`
    : vf1ComplyWorksAccountSelectorHint;

export const resolveVerisourceApplicationSwitcherLink = (organizationId?: string): string =>
  organizationId
    ? `/selectOrganization?organizationId=${encodeURIComponent(organizationId)}&returnTo=${encodeURIComponent(
        vf1VeriSourceAccountSelectorHint
      )}`
    : vf1VeriSourceAccountSelectorHint;

const isClientOrContractorFeatures = (features: ReadonlyArray<OrganizationFeature>): boolean =>
  features.includes(Client) ||
  features.includes(Subscriber) ||
  features.includes(Subcontractor) ||
  features.includes(PrimeContractor);

const hasCProNavigationFeature = ({ features }: ReadonlyUserOrganization): boolean =>
  features.includes(CProNavigationToVeriforceOne);

const isChasClientOrContractorOrganization = (features: ReadonlyArray<OrganizationFeature>): boolean =>
  isClientOrContractorFeatures(features) && isComplianceRegionCHAS(features);

export const shouldDisplayApplicationSwitcher = (
  userOrganizations: ReadonlyArray<ReadonlyUserOrganization> | null
): boolean => {
  if (!userOrganizations) {
    return false;
  }

  if (userOrganizations.length === 0) {
    return true;
  }

  const hasClientOrContractorAssociation = userOrganizations.some(userOrganization =>
    isClientOrContractorFeatures(userOrganization.features)
  );

  if (!hasClientOrContractorAssociation) {
    return true;
  }

  const hasCProToVf1Navigation = userOrganizations.some(userOrganization => hasCProNavigationFeature(userOrganization));

  if (!hasCProToVf1Navigation) {
    return false;
  }

  const hasOnlyChasClientOrContractorOrganizations = userOrganizations.every(userOrganization =>
    isChasClientOrContractorOrganization(userOrganization.features)
  );

  return !hasOnlyChasClientOrContractorOrganizations;
};

export function useUserMenuLink(
  userOrganizations: ReadonlyArray<ReadonlyUserOrganization> | null = null,
  organizationId?: string
): UseUserMenuLinkResult {
  const { hasGlobalPermission, hasUserPermission } = useUserPermission();

  const fallbackOrganizationId = React.useMemo(() => {
    if (organizationId) {
      return organizationId;
    }

    if (!userOrganizations?.length) {
      return undefined;
    }

    return userOrganizations.find(userOrganization => userOrganization.isPrimary)?.id ?? userOrganizations[0].id;
  }, [organizationId, userOrganizations]);

  const complyWorksApplicationSwitcherLink = React.useMemo(
    () => resolveApplicationSwitcherLink(fallbackOrganizationId),
    [fallbackOrganizationId]
  );
  const verisourceApplicationSwitcherLink = React.useMemo(
    () => resolveVerisourceApplicationSwitcherLink(fallbackOrganizationId),
    [fallbackOrganizationId]
  );

  const shouldShowCProApplicationSwitcher = React.useMemo(() => shouldDisplayApplicationSwitcher(userOrganizations), [
    userOrganizations
  ]);

  const { instructorPortalLink, shouldShowInstructorPortal } = React.useMemo(() => {
    const isAdmin = hasGlobalPermission ? hasGlobalPermission(Manage, Organizations) : false;
    if (isAdmin) {
      return { instructorPortalLink: '', shouldShowInstructorPortal: false };
    }

    const isInstructor = hasUserPermission ? hasUserPermission(Manage, Classes) : false;
    const link = isInstructor ? instructorPortalRoute : instructorMarketingPageRoute;

    return {
      instructorPortalLink: link,
      shouldShowInstructorPortal: true
    };
  }, [hasGlobalPermission, hasUserPermission]);

  return {
    complyWorksApplicationSwitcherLink,
    verisourceApplicationSwitcherLink,
    shouldShowApplicationSwitcher: shouldShowCProApplicationSwitcher,
    instructorPortalLink,
    shouldShowInstructorPortal
  };
}
